import type React from "react";
import { useCallback } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { MailIcon, SendIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { H5, H6, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import { Pagination } from "simplydo/react/ui/pagination";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import NotificationItem, {
  NotificationItemContainer,
} from "./NotificationItems";
import { useTrayStatus } from "./index";
import type { AnnouncementNotification, AppNotification } from "./types";

type NotificationTrayProps = {
  page: number;
  total: number;
  loading: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  invitations: OpenAPI.GET<`/invitations`>["response"]["invitations"];
  acceptableInvitations: OpenAPI.GET<`/invitations`>["response"]["invitations"];
  notifications: AppNotification[];
  setAnnouncementNotification: React.Dispatch<
    React.SetStateAction<AnnouncementNotification | null>
  >;
  setAnnouncementModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 7px;
`;

const StyledContent = styled.div`
  overflow: auto;
  padding-right: 0.5rem;
`;

const Notifications = ({
  acceptableInvitations,
  invitations,
  loading,
  notifications,
  page,
  setPage,
  total,
  setAnnouncementNotification,
  setAnnouncementModalOpen,
}: NotificationTrayProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useCurrentUser();
  const [_, setTrayStatus] = useTrayStatus();

  const onInvitationHandled = useCallback((invitationId: string) => {
    api.setQueryData(api.invitations.get, [], (prev) => ({
      ...prev,
      invitations: (prev?.invitations || []).filter(
        (i) => i._id !== invitationId,
      ),
    }));
  }, []);

  const markAsInteracted = useCallback(
    (notificationId: string) => {
      setTrayStatus({ open: false });
      api.notifications.markAsInteracted(
        notificationId,
        () => {
          api.setQueryData(api.notifications.get, [], (prev) => ({
            ...prev,
            notifications: (prev?.notifications || []).map((notification) =>
              notification._id === notificationId
                ? { ...notification, interacted: true }
                : notification,
            ),
          }));
        },
        () => {},
      );
    },
    [setTrayStatus],
  );

  return (
    <>
      {loading ? <Loader /> : null}
      {!loading && notifications.length ? (
        <>
          <StyledContent>
            {!loading && acceptableInvitations.length ? (
              <Link
                to="/invitations"
                onClick={() => setTrayStatus({ open: false })}
              >
                <NotificationItemContainer>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 60,
                      paddingRight: 7,
                    }}
                  >
                    <div
                      style={{
                        background: theme.colors.primary.softer,
                        padding: "0.5rem",
                        borderRadius: "50%",
                        position: "relative",
                      }}
                    >
                      <Label
                        size="small"
                        color="blue"
                        style={{ position: "absolute", top: -4, right: -6 }}
                        circular
                      >
                        {acceptableInvitations.length}
                      </Label>
                      <Icon icon={MailIcon} size="large" color="primary" />
                    </div>
                  </div>
                  <div>
                    <strong>Invitations</strong>
                    <p>
                      There{" "}
                      {util.pluralise(
                        acceptableInvitations.length,
                        "is",
                        "are",
                        false,
                      )}{" "}
                      {util.pluralise(
                        acceptableInvitations.length,
                        "invitation",
                        "invitations",
                      )}{" "}
                      waiting for you.
                    </p>
                  </div>
                </NotificationItemContainer>
              </Link>
            ) : null}
            {notifications.map((notification) => (
              <NotificationItem
                setAnnouncementNotification={setAnnouncementNotification}
                setAnnouncementModalOpen={setAnnouncementModalOpen}
                notification={notification}
                user={user}
                key={notification._id}
                markNotificationAsInteracted={markAsInteracted}
                invitations={invitations}
                onInvitationHandled={onInvitationHandled}
              />
            ))}
          </StyledContent>
          <StyledPaginationContainer>
            <Pagination
              totalPages={Math.ceil(total / 30)}
              activePage={page}
              ellipsisItem={null}
              firstItem={null}
              lastItem={null}
              onPageChange={(_e, { activePage }) =>
                setPage(activePage as number)
              }
            />
          </StyledPaginationContainer>
        </>
      ) : null}
      {!loading && !notifications.length ? (
        <div>
          <EmptyBox>
            <H5>{t("notifications.empty")}</H5>
          </EmptyBox>
        </div>
      ) : null}
    </>
  );
};

export default Notifications;
